import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { WFAlertHistoryRecord } from 'src/api/wf/models/WfAlertHistory';
import { WfHistoryDetail } from 'src/api/wf/models/WfHistoryDetail';
import useWfAlertHistoryService from 'src/api/wf/service/WfAlertHistoryService';

import { formatJpnDate } from 'src/common/formatJpnDate/formatJpnDate';
import { LoadingSpinner } from 'src/ui/wf/common/loading/LoadingSpinner';
import { FormRow } from 'src/ui/wf/common/form/FormRow';
import { formatDateTimeTwoLines } from 'src/ui/wf/common/util/dateTimeTwoLines';

export type AlertHistoryDetailProps = {
  data: WFAlertHistoryRecord;
};

const toDisplayText = (value: unknown) => {
  if (value === null || value === undefined) return '';

  const trimmed = String(value).trim();
  if (trimmed === '' || trimmed === 'null') return '';

  return trimmed;
};

function WF_4_7_1_AlertHistoryExpandableComponent({ data }: AlertHistoryDetailProps) {
  const { t } = useTranslation();
  const { fetchHistoryDetails } = useWfAlertHistoryService();

  const [details, setDetails] = useState<WfHistoryDetail[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    const loadHistoryDetails = async () => {
      setIsLoading(true);
      setError(false);
      try {
        const detailData = await fetchHistoryDetails(data.alertHistoryId);
        setDetails(detailData);
      } catch (err) {
        console.error('Failed to load history details:', err);
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };
    loadHistoryDetails();
  }, [data.alertHistoryId, fetchHistoryDetails]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !details || details.length === 0) {
    return <div className="p-4 text-sm text-gray-500">{t('wf.common.noResults')}</div>;
  }

  return (
    <div className="bg-gray-50 p-4">
      {details.map((detail, index) => (
        <React.Fragment key={detail.id}>
          {details.length > 1 && (
            <div className="mb-2 border-b border-gray-300 pb-1 text-sm font-semibold text-gray-700">
              #{index + 1}
            </div>
          )}
          <div className="grid grid-cols-1 gap-1 md:grid-cols-3">
            <FormRow title={t('wf.alertHistory.alertCategory')}>
              <p className="text-sm">{toDisplayText(detail.alertCategory)}</p>
            </FormRow>
            <FormRow title={t('wf.alertHistory.errorDateTime')}>
              <div className="text-sm">{formatDateTimeTwoLines(detail.errorDateTime)}</div>
            </FormRow>
            <FormRow title={t('wf.alertHistory.recoveryDateTime')}>
              <div className="text-sm">{formatDateTimeTwoLines(detail.recoveryDateTime)}</div>
            </FormRow>

            <FormRow title={t('wf.alertHistory.alertDateTime')}>
              <div className="text-sm">{formatDateTimeTwoLines(detail.alertDateTime)}</div>
            </FormRow>
            <FormRow title={t('wf.alertHistory.dispatchDateTime')}>
              <div className="text-sm">{formatDateTimeTwoLines(detail.dispatchDateTime)}</div>
            </FormRow>
            <FormRow title={t('wf.alertHistory.arrivalDateTime')}>
              <div className="text-sm">{formatDateTimeTwoLines(detail.arrivalDateTime)}</div>
            </FormRow>

            <FormRow title={t('wf.alertHistory.completionDateTime')}>
              <p className="text-sm">
                {detail.completionDateTime ? formatJpnDate({ iso: detail.completionDateTime }) : ''}
              </p>
            </FormRow>
            <FormRow title={t('wf.alertHistory.recoveryAlertStatus')}>
              <p className="text-sm">
                {detail.recoveryAlertStatus
                  ? t('wf.alertHistory.checkMark')
                  : t('wf.alertHistory.crossMark')}
              </p>
            </FormRow>
            <FormRow title={t('wf.alertHistory.directCall')}>
              <p className="text-sm">{toDisplayText(detail.directCall)}</p>
            </FormRow>

            <FormRow title={t('wf.alertHistory.alertComment')}>
              <p className="whitespace-pre-wrap text-sm">{toDisplayText(detail.alertComment)}</p>
            </FormRow>
            <FormRow title={t('wf.alertHistory.receivedContent')}>
              <p className="whitespace-pre-wrap text-sm">{toDisplayText(detail.receivedContent)}</p>
            </FormRow>

            <FormRow title={t('wf.alertHistory.callerName')}>
              <p className="text-sm">{toDisplayText(detail.callerName)}</p>
            </FormRow>
            <FormRow title={t('wf.alertHistory.callerPhoneNumber')}>
              <p className="text-sm">{toDisplayText(detail.callerPhoneNumber)}</p>
            </FormRow>
            <FormRow title={t('wf.alertHistory.callerCategory')}>
              <p className="text-sm">{toDisplayText(detail.callerCategory)}</p>
            </FormRow>

            <FormRow title={t('wf.alertHistory.reportTo1Name')}>
              <p className="text-sm">{toDisplayText(detail.reportTo1Name)}</p>
            </FormRow>
            <FormRow title={t('wf.alertHistory.reportTo1Contact')}>
              <p className="text-sm">{toDisplayText(detail.reportTo1Contact)}</p>
            </FormRow>
            <FormRow title={t('wf.alertHistory.reportTo1Category')}>
              <p className="text-sm">{toDisplayText(detail.reportTo1Category)}</p>
            </FormRow>

            <FormRow title={t('wf.alertHistory.reportTo2Name')}>
              <p className="text-sm">{toDisplayText(detail.reportTo2Name)}</p>
            </FormRow>
            <FormRow title={t('wf.alertHistory.reportTo2Contact')}>
              <p className="text-sm">{toDisplayText(detail.reportTo2Contact)}</p>
            </FormRow>
            <FormRow title={t('wf.alertHistory.reportTo2Category')}>
              <p className="text-sm">{toDisplayText(detail.reportTo2Category)}</p>
            </FormRow>
          </div>
          {index < details.length - 1 && <div className="my-4 border-t border-gray-300" />}
        </React.Fragment>
      ))}
    </div>
  );
}

export default WF_4_7_1_AlertHistoryExpandableComponent;
